import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const LanguagesForm = ({ formData, setFormData, isEditing }) => {
  const { t } = useTranslation();

  const toEntries = (obj) =>
    obj && typeof obj === "object" && !Array.isArray(obj) ? Object.entries(obj) : [];

  const allLanguages = toEntries(t("languageslist", { returnObjects: true }));
  const levels = toEntries(t("levels", { returnObjects: true }));
  const levels2 = toEntries(t("levels2", { returnObjects: true }));

  const languageRows =
    formData?.languages?.length > 0
      ? formData.languages
      : [{ name: "", level: "", level2: "" }];

  const [showLang, setShowLang] = useState(null);
  const [showLevel, setShowLevel] = useState(null);
  const [showLevel2, setShowLevel2] = useState(null);

  const handleArrayChange = (index, key, value) => {
    const updated = [...languageRows];
    updated[index][key] = value;
    setFormData({ languages: updated });

    if (key === "name") setShowLang(null);
    if (key === "level") setShowLevel(null);
    if (key === "level2") setShowLevel2(null);
  };

  const addField = () => {
    setFormData({
      languages: [...languageRows, { name: "", level: "", level2: "" }],
    });
  };

  const removeField = (i) => {
    const next = languageRows.filter((_, idx) => idx !== i);
    setFormData({
      languages: next.length > 0 ? next : [{ name: "", level: "", level2: "" }],
    });
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      {isEditing ? (
        // ✍️ Режим редактирования
        languageRows.map((lang, i) => (
          <div key={i} className="flex flex-col gap-4 border-b pb-3">
            {/* Язык */}
            <div className="relative w-full">
              <div className="flex justify-start items-center gap-2">
                <span className="text-red-500">*</span>
                <input
                  type="text"
                  readOnly
                  placeholder={t("language")}
                  className="w-full py-2 px-4 border rounded-md outline-none cursor-pointer focus:border-[#006674]"
                  value={lang.name ? t(`languageslist.${lang.name}`) : ""}
                  onClick={() => setShowLang(showLang === i ? null : i)}
                />
              </div>
              {showLang === i && (
                <div className="absolute bg-white border rounded-md p-2 mt-1 z-[200] shadow-lg w-full max-h-60 overflow-y-auto">
                  {allLanguages.map(([key, label]) => (
                    <div
                      key={key}
                      className="p-2 hover:bg-[#006674] hover:text-white cursor-pointer rounded"
                      onClick={() => handleArrayChange(i, "name", key)}
                    >
                      {label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="w-full flex justify-between items-center gap-3">
              {/* Уровень 1 */}
              <div className="relative w-full">
                <label className="text-sm ml-2 text-[#006674]">
                  {t("languageselect1")}
                </label>
                <div className="flex justify-start items-center gap-2">
                  <span className="text-red-500">*</span>
                  <input
                    type="text"
                    readOnly
                    placeholder={t("languageselect1")}
                    className="w-full py-2 px-4 border rounded-md outline-none cursor-pointer focus:border-[#006674] focus:shadow-xl"
                    value={lang.level ? t(`levels.${lang.level}`) : ""}
                    onClick={() => setShowLevel(showLevel === i ? null : i)}
                  />
                </div>

                {showLevel === i && (
                  <div className="absolute bg-white border rounded-md p-2 mt-1 z-50 shadow w-full">
                    {levels.map(([key, label]) => (
                      <div
                        key={key}
                        className="p-2 hover:bg-[#006674] hover:text-white cursor-pointer rounded"
                        onClick={() => handleArrayChange(i, "level", key)}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Уровень 2 */}
              <div className="relative w-full">
                <label className="text-sm ml-2 text-[#006674]">
                  {t("languageselect2")}
                </label>
                <div className="flex justify-start items-center gap-2">
                  <span className="text-red-500">*</span>
                  <input
                    type="text"
                    readOnly
                    placeholder={t("languageselect2")}
                    className="w-full py-2 px-4 border rounded-md outline-none cursor-pointer focus:border-[#006674] focus:shadow-xl"
                    value={lang.level2 ? t(`levels2.${lang.level2}`) : ""}
                    onClick={() => setShowLevel2(showLevel2 === i ? null : i)}
                  />
                </div>
                {showLevel2 === i && (
                  <div className="absolute bg-white border rounded-md p-2 mt-1 z-50 shadow w-full">
                    {levels2.map(([key, label]) => (
                      <div
                        key={key}
                        className="p-2 hover:bg-[#006674] hover:text-white cursor-pointer rounded"
                        onClick={() => handleArrayChange(i, "level2", key)}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Кнопки */}
            <div className="flex gap-2 mt-2">
              {i >= 1 && (
                <button
                  type="button"
                  onClick={() => removeField(i)}
                  className="min-w-[40px] h-[40px] flex justify-center items-center bg-red-600 text-white rounded-full"
                >
                  <Trash2 size={18} />
                </button>
              )}
              {i === languageRows.length - 1 && (
                <button
                  type="button"
                  onClick={addField}
                  className="min-w-[40px] h-[40px] flex justify-center items-center bg-[#006674] text-white rounded-full"
                >
                  <Plus size={18} />
                </button>
              )}
            </div>
          </div>
        ))
      ) : (
        // 📋 Табличный вид при просмотре
        <table className="w-[400px] max-sm:w-full mb-6 border border-gray-300">
          <thead>
            <tr className="bg-[#006674] text-white">
              <th className="text-sm px-2 py-1 border border-gray-300">
                {t("language")}
              </th>
              <th className="text-sm px-2 py-1 border border-gray-300">
                {t("languageselect1")}
              </th>
              <th className="text-sm px-2 py-1 border border-gray-300">
                {t("languageselect2")}
              </th>
            </tr>
          </thead>
          <tbody>
            {languageRows
              .filter((lang) => lang.name?.trim())
              .map((lang, i) => (
              <tr key={i}>
                <td className="px-2 text-center py-1 border border-gray-300">
                  {lang.name ? t(`languageslist.${lang.name}`) : "-"}
                </td>
                <td className="px-2 text-center py-1 border border-gray-300">
                  {lang.level ? t(`levels.${lang.level}`) : "-"}
                </td>
                <td className="px-2 text-center py-1 border border-gray-300">
                  {lang.level2 ? t(`levels2.${lang.level2}`) : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default LanguagesForm;
